import { useState, useMemo } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { FunnelSimple, Check, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * ColumnFilterPopover — Excel-style per-column filter dropdown.
 *
 * Props:
 *   filters   — full filter state object
 *   setFilters(next) — updater
 *   type      — "select" | "range" | "text"
 *   options   — for select: [{value, label}] (ComboResponseDto item shape)
 *   keys      — for range: ["priceMin","priceMax"] | for select: ["regionIds"] | text: ["code"]
 *   label     — column label
 */
export const ColumnFilter = ({
  filters, setFilters, type, options = [], keys, label,
}) => {
  const [q, setQ] = useState("");
  const active = useMemo(() => keys.some((k) => filters[k]), [filters, keys]);

  const upd = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const clear = () => upd(Object.fromEntries(keys.map((k) => [k, ""])));

  const filteredOpts = useMemo(() => {
    if (!q) return options;
    const s = q.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [options, q]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`col-filter-btn-${keys[0]}`}
          type="button"
          className={`inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
            active
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-secondary"
          }`}
          aria-label={`Filter ${label}`}
        >
          <FunnelSimple size={11} weight={active ? "fill" : "bold"} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3 rounded-xl"
        data-testid={`col-filter-popover-${keys[0]}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="label-tiny">{label}</div>
          {active && (
            <button
              onClick={clear}
              data-testid={`col-filter-clear-${keys[0]}`}
              className="text-[10px] font-semibold text-primary hover:underline"
            >
              Təmizlə
            </button>
          )}
        </div>

        {type === "text" && (
          <Input
            data-testid={`col-filter-input-${keys[0]}`}
            value={filters[keys[0]] || ""}
            onChange={(e) => upd({ [keys[0]]: e.target.value })}
            placeholder="Axtarın..."
            className="h-9 rounded-lg text-sm"
            autoFocus
          />
        )}

        {type === "range" && (
          <div className="flex gap-2">
            <Input
              data-testid={`col-filter-min-${keys[0]}`}
              type="number" inputMode="numeric" placeholder="Min"
              value={filters[keys[0]] || ""}
              onChange={(e) => upd({ [keys[0]]: e.target.value })}
              className="h-9 rounded-lg text-sm"
            />
            <Input
              data-testid={`col-filter-max-${keys[0]}`}
              type="number" inputMode="numeric" placeholder="Max"
              value={filters[keys[1]] || ""}
              onChange={(e) => upd({ [keys[1]]: e.target.value })}
              className="h-9 rounded-lg text-sm"
            />
          </div>
        )}

        {type === "select" && (
          <div>
            {options.length > 8 && (
              <div className="relative mb-2">
                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Axtar..."
                  className="pl-7 h-8 rounded-lg text-xs"
                />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto -mx-1">
              <button
                type="button"
                onClick={() => upd({ [keys[0]]: "" })}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm hover:bg-secondary ${!filters[keys[0]] ? "text-primary font-semibold" : ""}`}
              >
                <span>Hamısı</span>
                {!filters[keys[0]] && <Check size={12} weight="bold" />}
              </button>
              {filteredOpts.map((o) => {
                const sel = String(filters[keys[0]]) === String(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`col-filter-opt-${keys[0]}-${o.value}`}
                    onClick={() => upd({ [keys[0]]: sel ? "" : String(o.value) })}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm hover:bg-secondary ${sel ? "text-primary font-semibold" : ""}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {sel && <Check size={12} weight="bold" className="shrink-0" />}
                  </button>
                );
              })}
              {filteredOpts.length === 0 && (
                <div className="px-2 py-4 text-center text-xs text-muted-foreground">Boş</div>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
